import React, { useState } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import colors from "../../../theme/colors";
import useFullSubmission from "../useFullSubmission";
import T from "../submissionTableTokens";
import formatDate from "../../../_src_shared/utils/functions/formatDate";
import SendToBorrowerActions from "./SendToBorrowerActions";
import SubmissionSummaryTab from "./SubmissionSummaryTab";
import SubmissionPdfTab from "./SubmissionPdfTab";
import SubmissionJsonTab from "./SubmissionJsonTab";
import {
  AssignToLoanChip,
  ContainedActionChip,
  CreateLoanChip,
  LoanLinkChip,
  canCreateLoanFrom,
  submissionTypeLong,
} from "./tableChips";
import { downloadSubmissionPdf } from "../submissionPdf";
import {
  LANES,
  submissionBorrowerName,
  submissionIsAssigned,
} from "../submissionHelpers";

const TABS = [
  { value: "summary", label: "Summary" },
  { value: "pdf", label: "PDF" },
  { value: "json", label: "JSON" },
];

// Names what comes down, so the chip reads as an action on this submission
// rather than a generic download sitting next to two loan actions.
const downloadLabel = (row) =>
  row?.__type === LANES.CREDIT_AUTHORIZATION ? "Download Auth" : "Download App";

/**
 * The expanded row: one action bar, then the submission in three views.
 *
 * The bar reads left to right as context → state → actions, and the actions
 * don't move between the assigned and unassigned cases — only the state chip
 * swaps for the Assign button, so the Send and Download controls stay where the
 * hand already is.
 */
const SubmissionDetailPanel = (params) => {
  const { data: row } = params;
  // Pick handlers out by name rather than spreading the rest of `params`. AG
  // Grid also passes `api`, `node`, `context` etc., and those are deeply cyclic.
  const { onAssign, onCreateLoan, busyId } = params;
  const [tab, setTab] = useState("summary");
  // Everything the panel DISPLAYS reads from this. The loan actions below
  // deliberately keep passing `row` — see the note at their call sites.
  const submission = useFullSubmission(row);

  if (!row) return null;

  const assigned = submissionIsAssigned(submission);
  const busy = busyId === row._id;

  return (
    // Three layers: the blue gutter carries the expanded row's tint down the
    // whole panel, and the white card floats inside it holding the content — so
    // the open row reads as a container and the submission reads as the thing
    // inside it.
    <Box
      sx={{
        background: T.expandedBg,
        px: 2,
        // 24px top and bottom — the blue gutter reads as one block with the
        // expanded row above it, so it needs even breathing room at both ends.
        py: "24px",
        // AG Grid suppresses text selection across the grid, which carries into
        // the detail row — so none of the submission's own data could be
        // highlighted or copied. This is a read-and-copy surface; re-enable it.
        userSelect: "text",
        WebkitUserSelect: "text",
        cursor: "auto",
      }}
    >
      {/* Above the card, on the blue: WHO this is, and the actions aimed at the
          person — sending them another link is about them, not about the
          submission below. */}
      <Flx centerY wrap gap={1.5} sx={{ px: "4px", pb: 1.5 }}>
        {/* The panel's heading — it names who everything below belongs to, so
            it carries heading weight rather than the 13px the card's rows use. */}
        <Txt
          sx={{
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: "-.01em",
            color: colors.slate900,
            flex: 1,
            minWidth: 160,
          }}
        >
          {submissionBorrowerName(submission) || "Unnamed borrower"}
        </Txt>
        {/* Safe on the full document: submissionToSendPrefill is an explicit
            allowlist and never reaches for SSN or DOB — the server rejects a
            PII prefill with a 400. */}
        <SendToBorrowerActions row={submission} />
      </Flx>

      <Box
        sx={{
          background: "#fff",
          border: `1px solid ${colors.slate200}`,
          borderRadius: "11px",
          boxShadow: "0 1px 4px rgba(16,30,51,.05)",
          overflow: "hidden",
        }}
      >
        {/* First row in the card: WHAT this is, and the actions that act on it.
            Same chips as the Status column, so an action never appears as two
            differently-styled controls. */}
        <Flx centerY wrap gap={1.5} sx={{ p: 2 }}>
          <Txt
            sx={{
              fontSize: 15,
              fontWeight: 650,
              color: colors.slate900,
              flex: 1,
              minWidth: 160,
            }}
          >
            {submissionTypeLong(submission)},{" "}
            {formatDate(submission.submission_date, "—", "MMM D, YYYY")}
          </Txt>

          <Flx centerY wrap gap={0.875} sx={{ flexShrink: 0 }}>
            {/* The loan state and the action that changes it share one slot, so
                they never appear in two places. Assigned reads as a labelled
                value; unassigned is the bare action, which already says "Assign
                To Loan" on its face. */}
            {assigned ? (
              <Flx centerY gap={0.75}>
                <Txt sx={{ fontSize: 12.5, color: colors.slate500 }}>
                  Assigned To:
                </Txt>
                <LoanLinkChip row={submission} />
              </Flx>
            ) : (
              // Still `row`, not `submission`: assign_to_deal POSTs the object
              // it's handed straight onto the deal, and there's no reason to
              // widen what that writes. The PDF those flows file is a separate
              // step that fetches the full document itself — see
              // attachSubmissionPdf in SubmissionsList.
              <Flx centerY wrap gap={0.625}>
                <AssignToLoanChip onClick={() => !busy && onAssign(row)} />
                {onCreateLoan && canCreateLoanFrom(row) && (
                  <CreateLoanChip onClick={() => !busy && onCreateLoan(row)} />
                )}
              </Flx>
            )}
            {/* The manual download DOES get the full copy — someone clicking it
                is asking for this one submission's record. */}
            <ContainedActionChip
              icon={<DownloadOutlinedIcon sx={{ fontSize: 13 }} />}
              onClick={() => downloadSubmissionPdf(submission)}
            >
              {downloadLabel(submission)}
            </ContainedActionChip>
          </Flx>
        </Flx>

        {/* Tabs — views OF this submission. */}
        <Flx
          between
          centerY
          wrap
          gap={1.5}
          sx={{
            // px: 0.5,
            // px: "18px",
            //
            background: colors.slate50,
            borderTop: `1px solid ${colors.slate100}`,
          }}
        >
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            // Selected state in brand blue. The theme's default tab colour is
            // the dark navy it uses for text, which reads as "bold" rather than
            // "selected" against the other two labels.
            sx={{
              minHeight: 0,
              "& .MuiTabs-indicator": {
                backgroundColor: (t) => t.palette.primary.main,
              },
              "& .MuiTab-root": {
                minHeight: 0,
                textTransform: "none",
                fontSize: 12.5,
                fontWeight: 550,
                px: 1.5,
                py: 1.375,
              },
              "& .MuiTab-root.Mui-selected": {
                // color: "primary.main",
                fontWeight: 650,
              },
            }}
          >
            {TABS.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} />
            ))}
          </Tabs>
        </Flx>

        <Box sx={{ p: 2, borderTop: `1px solid ${colors.slate100}` }}>
          {tab === "summary" && <SubmissionSummaryTab row={submission} />}
          {/* Mounted only while selected, so expanding a row doesn't render a
              PDF nobody asked to see. */}
          {tab === "pdf" && <SubmissionPdfTab row={submission} />}
          {tab === "json" && <SubmissionJsonTab row={submission} />}
        </Box>
      </Box>
    </Box>
  );
};

export default SubmissionDetailPanel;
